export type Role = "superadmin" | "admin" | "manager" | "operator";

export type Permission =
  | "pos.sell"
  | "orders.read"
  | "orders.void"
  | "orders.refund"
  | "orders.reprint"
  | "cash-drawer.open"
  | "eod.close"
  | "reports.read"
  | "reports.export"
  | "menu.manage"
  | "users.manage"
  | "roles.manage"
  | "devices.manage"
  | "settings.manage"
  | "sync.manage"
  | "sync.history.read"
  | "sync.security_metadata.read"
  | "sync.conflicts.resolve"
  | "platform.stores.manage"
  | "platform.accounts.manage"
  | "platform.audit.read";

export type AuthMethod = "admin-password" | "operator-pin";

export type TenantContext = {
  storeId: string;
  actorId: string;
  deviceId: string;
  role: Role;
};

export type AuthSession = {
  sessionId: string;
  storeId: string;
  actorId: string;
  /** Operator sessions have a POS device; Admin sessions are browser sessions without a registered device. */
  deviceId: string | null;
  role: Role;
  displayName: string;
  expiresAt: string;
};

export type PlatformSession = {
  sessionId: string;
  actorId: string;
  displayName: string;
  role: "superadmin";
  expiresAt: string;
};

export type PlatformLoginRequest = {
  method: "superadmin-password";
  email: string;
  secret: string;
};

export type AdminPasswordLoginRequest = {
  method: "admin-password";
  email: string;
  secret: string;
  storeId: string;
  /** Optional internal device context; Admin UI does not collect or send it. */
  deviceId?: string;
};

export type AdminAuthOptionsRequest = {
  method: "admin-password";
  email: string;
  secret: string;
};

export type AdminAuthOptions = {
  stores: BootstrapStoreOption[];
};

export type UnifiedAdminAuthOptions =
  | { scope: "platform"; stores: [] }
  | { scope: "store"; stores: BootstrapStoreOption[] };

export type UnifiedAdminSession =
  | { scope: "platform"; session: PlatformSession }
  | { scope: "store"; session: AuthSession };

export type UnifiedAdminAuthResult =
  | { scope: "platform"; session: PlatformSession; token: string }
  | { scope: "store"; session: AuthSession; token: string };

export type StoreSwitchRequest = {
  storeId: string;
};

export type OperatorPinLoginRequest = {
  method: "operator-pin";
  pin: string;
  storeId: string;
  deviceId: string;
};

export type LoginRequest = AdminPasswordLoginRequest | OperatorPinLoginRequest;

export type OperatorDeviceContext = {
  storeId: string;
  deviceId: string;
  clientId: string;
  label: string;
};

export type BootstrapStoreOption = {
  storeId: string;
  name: string;
};

export type DeviceBootstrapAuthorizationRequest = {
  method: "owner-password";
  email: string;
  secret: string;
};

export type DeviceBootstrapAuthorization = {
  stores: BootstrapStoreOption[];
  canCreateStore: boolean;
};

export type DeviceBootstrapStoreSelection =
  | { mode: "existing"; storeId: string }
  | {
      mode: "new";
      /** The canonical Store ID is assigned once at Store creation. */
      storeId: string;
      name: string;
      region: string;
      timeZone: string;
      currency: "GBP" | "USD" | "EUR" | "MOP";
      businessDayStartMinutes: number;
      deviceLabel: string;
    };

export type DeviceBootstrapRequest = {
  method: "owner-password";
  email: string;
  secret: string;
  clientId: string;
  storeSelection: DeviceBootstrapStoreSelection;
};

export type DeviceOperatorSnapshot = {
  actorId: string;
  displayName: string;
  role: Role;
  active: boolean;
  membershipActive: boolean;
  /** Server PBKDF2 verifier; this is never a plaintext PIN. */
  pinVerifier: string;
  updatedAt: string;
};

export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  superadmin: ["platform.stores.manage", "platform.accounts.manage", "platform.audit.read"],
  admin: [
    "pos.sell",
    "orders.read",
    "orders.void",
    "orders.refund",
    "orders.reprint",
    "cash-drawer.open",
    "eod.close",
    "reports.read",
    "reports.export",
    "menu.manage",
    "users.manage",
    "roles.manage",
    "devices.manage",
    "settings.manage",
    "sync.manage",
    "sync.history.read",
    "sync.security_metadata.read",
    "sync.conflicts.resolve",
  ],
  manager: [
    "pos.sell",
    "orders.read",
    "orders.void",
    "settings.manage",
  ],
  operator: ["pos.sell", "orders.read", "orders.void"],
};

export const PERMISSION_LABELS: Record<Permission, string> = {
  "pos.sell": "Create sales",
  "orders.read": "View orders",
  "orders.void": "Void orders",
  "orders.refund": "Refund orders",
  "orders.reprint": "Reprint receipts",
  "cash-drawer.open": "Open cash drawer",
  "eod.close": "Close end of day",
  "reports.read": "View reports",
  "reports.export": "Export reports",
  "menu.manage": "Manage menu",
  "users.manage": "Manage users",
  "roles.manage": "Manage roles",
  "devices.manage": "Manage devices",
  "settings.manage": "Manage settings",
  "sync.manage": "Manage sync",
  "sync.history.read": "View Store sync history",
  "sync.security_metadata.read": "View sync security metadata",
  "sync.conflicts.resolve": "Resolve sync conflicts",
  "platform.stores.manage": "Manage platform Stores",
  "platform.accounts.manage": "Manage platform accounts",
  "platform.audit.read": "View platform audit",
};

export const PLATFORM_PERMISSIONS: readonly Permission[] = [
  "platform.stores.manage",
  "platform.accounts.manage",
  "platform.audit.read",
];

export function hasPermission(role: Role, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role].includes(permission);
}

export function hasPlatformPermission(role: Role, permission: Permission): boolean {
  return role === "superadmin" && PLATFORM_PERMISSIONS.includes(permission);
}

export function assertTenantContext(context: TenantContext): void {
  if (!context.storeId || !context.actorId || !context.deviceId || !context.role) {
    throw new Error("storeId, actorId, deviceId and role are required for tenant access");
  }
}
